import { apiClient, apiDownload, throwApiError } from './apiClient';
import type { GridInsert, GridUpdate, SizeSelect } from '@urbanmetrix/sdk';

export type GridReplacePayload = {
  name: string;
  description: string | null;
  params: NonNullable<GridInsert['params']>;
  geodata: Record<string, unknown>;
};

export type GridClonePayload = {
  name?: string;
};

export type GridCloneResult = {
  id: string;
  projectId: string;
  geodataId: string | null;
  simulationSetId: string | null;
};

type ApiResponse<T> = Promise<{ data: T; error: { value: unknown; status: number } | null }>;

type GridFileRecord = Record<string, unknown>;
type GridFilesEndpoint = {
  'imported-files': {
    get: () => ApiResponse<GridFileRecord[]>;
  } & ((params: { importedFileId: string }) => {
    get: () => ApiResponse<GridFileRecord>;
    patch: (payload: { name?: string }) => ApiResponse<GridFileRecord>;
  });
  'exported-files': {
    get: () => ApiResponse<GridFileRecord[]>;
  } & ((params: { exportedFileId: string }) => {
    get: () => ApiResponse<GridFileRecord>;
    patch: (payload: { name?: string }) => ApiResponse<GridFileRecord>;
    delete: () => ApiResponse<GridFileRecord>;
  });
};

const gridExtensions = apiClient.api.grids as unknown as {
  volume: { post: (payload: GridVolumeCalculation) => ApiResponse<{ gridVolume: number }> };
  (params: { id: string }): {
    clone: { post: (payload: GridClonePayload) => ApiResponse<GridCloneResult> };
    'sensor-points': {
      get: (options: { query: { gridResolution: number } }) => ApiResponse<{ sensorPoints: number }>;
    };
    'volume-usage': { get: () => ApiResponse<GridVolumeUsage> };
    volume: { consume: { post: () => ApiResponse<GridVolumeUsage> } };
  };
};

export async function cloneGrid(id: string, payload: GridClonePayload = {}): Promise<GridCloneResult> {
  const response = await gridExtensions({ id }).clone.post(payload);
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function getGrids(projectId?: string) {
  const response = await apiClient.api.grids.get({
    query: projectId ? { projectId } : {},
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function getGrid(id: string) {
  const response = await apiClient.api.grids({ id }).get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export async function getGridSize(id: string): Promise<SizeSelect> {
  const response = await apiClient.api.grids({ id }).size.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }
  return response.data;
}

export type GridVolumeUsage = {
  gridVolume: number;
  paidVolume: number;
  volumeLimit: number;
  volumeCredits: number;
  volumeDifference: number;
};

export type GridVolumeCalculation = {
  analysisArea: Array<Array<{ x: number; y: number; z?: number }>>;
  gridHeight: number;
};

export async function calculateGridVolume(payload: GridVolumeCalculation): Promise<{ gridVolume: number }> {
  const response = await gridExtensions.volume.post(payload);
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function getGridSensorPoints(id: string, gridResolution: number): Promise<{ sensorPoints: number }> {
  const response = await gridExtensions({ id })['sensor-points'].get({ query: { gridResolution } });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function getGridVolumeUsage(id: string): Promise<GridVolumeUsage> {
  const response = await gridExtensions({ id })['volume-usage'].get();
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function consumeGridVolume(id: string): Promise<GridVolumeUsage> {
  const response = await gridExtensions({ id }).volume.consume.post();
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function addGrid(grid: GridInsert) {
  const response = await apiClient.api.grids.post(grid);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateGrid(id: string, grid: GridUpdate) {
  const response = await apiClient.api.grids({ id }).patch(grid);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function replaceGrid(id: string, grid: GridReplacePayload) {
  const response = await apiClient.api.grids({ id }).put(grid as never);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteGrid(id: string) {
  const response = await apiClient.api.grids({ id }).delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getGridExportedFiles(gridId: string) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)['exported-files'].get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function getGridImportedFiles(gridId: string) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)['imported-files'].get();
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function getGridImportedFile(gridId: string, importedFileId: string) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)
    ['imported-files']({ importedFileId })
    .get();
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function downloadGridImportedFile(gridId: string, importedFileId: string) {
  return apiDownload(`/api/grids/${gridId}/imported-files/${importedFileId}/download`);
}

export async function updateGridImportedFile(gridId: string, importedFileId: string, importedFile: { name?: string }) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)
    ['imported-files']({ importedFileId })
    .patch(importedFile);
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function getGridExportedFile(gridId: string, exportedFileId: string) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)
    ['exported-files']({ exportedFileId })
    .get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function updateGridExportedFile(gridId: string, exportedFileId: string, exportedFile: { name?: string }) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)
    ['exported-files']({ exportedFileId })
    .patch(exportedFile);
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function deleteGridExportedFile(gridId: string, exportedFileId: string) {
  const response = await (apiClient.api.grids({ id: gridId }) as never as GridFilesEndpoint)
    ['exported-files']({ exportedFileId })
    .delete();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}
